<script setup>
import { computed } from "vue"

// Only icons referenced by the app live in `@/icons/basicons/` so
// Vite does not hash the unused catalog. Copy a file from
// `icons/catalog/` (or basicons.com) to add a name.
const icons = import.meta.glob("@/icons/basicons/*.svg", {
  query: "?raw",
  import: "default",
  eager: true,
})

// Vite returns alias-relative keys (e.g. `/icons/basicons/x.svg`),
// not absolute project paths. Index by the bare file name so the
// lookup stays correct regardless of how `@` is configured.
const iconsByName = Object.fromEntries(
  Object.entries(icons).map(([path, content]) => {
    const name = path.split("/").pop().replace(/\.svg$/, "")
    return [name, content]
  }),
)

const props = defineProps({
  name: { type: String, required: true },
  size: { type: [String, Number], default: 20 },
  label: { type: String, default: null },
})

const svg = computed(() => {
  const found = iconsByName[props.name]
  if (!found && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[Icon] Unknown icon name: "${props.name}"`)
  }
  return found ?? null
})

const sizeStyle = computed(() => {
  const s = typeof props.size === "number" ? `${props.size}px` : props.size
  return { width: s, height: s }
})
</script>

<template>
  <span
    v-if="svg"
    class="inline-flex items-center justify-center text-current shrink-0"
    :style="sizeStyle"
    :role="label ? 'img' : undefined"
    :aria-label="label"
    :aria-hidden="label ? null : 'true'"
    v-html="svg"
  />
  <!-- silently render nothing when the name is unknown; surfaces the
       mistake via empty space instead of crashing the page. -->
</template>

<style scoped>
.inline-flex :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
